import { ArrowRight, CalendarDays } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { contentFor, formatPrice, type Currency, type Locale, type Product } from "@nh/shared";
import { Button } from "@/shared/ui/core";
import { priceFor } from "@/features/shop/shop.catalog";
import { usePriceViewer } from "@/features/shop/usePriceViewer";
import { useAuth } from "@/features/auth/auth.context";
import { registerHrefFor } from "../packages.catalog";

/**
 * La tarjeta de una MEMBRESÍA (01 Arranque digital, 02 DUO SINERGIA,
 * 03 FUNDADOR ELITE): las que producción lista al frente de
 * `/shop/paquetes`. En el sistema nuevo no son `Package` —un paquete
 * trae productos del catálogo y estas no—, así que viven como productos
 * de tipo `membership` y aquí se pintan con la misma forma que en
 * producción: arte, nombre, precio, "30 días membresía", descripción,
 * beneficios y el botón de inscribirse.
 */
export function isMembership(product: Product): boolean {
  return product.type === "membership";
}

export function MembershipCard({
  product,
  currency,
  locale,
  numberLocale,
}: {
  product: Product;
  currency: Currency;
  locale: Locale;
  numberLocale: string;
}) {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const content = contentFor(product.translations, locale);
  const price = priceFor(product, currency, usePriceViewer());
  const days = product.validity?.durationDays;
  const href = isAuthenticated ? "/franquicias/adquirir" : registerHrefFor(product.slug);

  return (
    <article className="flex h-full flex-col overflow-hidden rounded-ds-xl border border-ds-border bg-white shadow-ds-xs transition-shadow duration-300 hover:shadow-ds-card">
      <div className="aspect-[3/2] w-full overflow-hidden bg-ds-lavender-bg">
        {product.imageUrl ? (
          <img src={product.imageUrl} alt={content.name} className="h-full w-full object-cover object-top" loading="lazy" />
        ) : null}
      </div>
      <div className="flex flex-1 flex-col p-6 sm:p-7">
        <h3 className="break-words text-[22px] font-semibold leading-tight tracking-tight text-ds-ink">
          {content.name}
        </h3>
        {price && (
          <p className="mt-3 flex items-baseline gap-2">
            <span className="text-[34px] font-light leading-none tracking-tight text-ds-ink">
              {formatPrice(price.cents, price.currency, numberLocale)}
            </span>
            <span className="text-[12px] font-semibold uppercase tracking-ds-label text-ds-ink-3">
              {price.currency}
            </span>
          </p>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          {/* Las membresías no dan puntos ni generan comisión (regla del
              10-sep-2026). El sistema viejo lo decía en la tarjeta y aquí
              se había perdido (Anahí, 30-sep). */}
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-[12px] font-semibold text-amber-800">
            {t("No comisionable y no puntos")}
          </span>
          {days ? (
            <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-ds-success-soft px-3 py-1 text-[12px] font-semibold text-ds-ink">
              <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
              {t("{{n}} días membresía", { n: days })}
            </span>
          ) : null}
        </div>
        {content.description && (
          <p className="mt-4 text-[14px] leading-[1.6] text-ds-ink-3">{content.description}</p>
        )}
        {content.benefits && content.benefits.length > 0 && (
          <div className="mt-5">
            <p className="text-[13px] font-semibold text-ds-ink">{t("Beneficios")}</p>
            <ul className="mt-2 space-y-1.5">
              {content.benefits.map((benefit) => (
                <li key={benefit} className="flex items-start gap-2 text-[13.5px] leading-snug text-ds-ink-3">
                  <span aria-hidden="true" className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-ds-violet-pop" />
                  <span className="min-w-0 break-words">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="mt-auto pt-6">
          <Button
            as={Link}
            to={href}
            size="lg"
            fullWidth
            aria-label={t("Inscribirme con {{nombre}}", { nombre: content.name })}
            rightIcon={<ArrowRight aria-hidden="true" />}
          >
            {t("Inscribirme")}
          </Button>
        </div>
      </div>
    </article>
  );
}
